import React, { useState } from 'react';
import find from 'lodash/find';
import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import startCase from 'lodash/startCase';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Image from 'components/Image';
import { images } from 'utils/icons';
import fieldSelection from '../index';
import DetailField from 'components/Details';
import { checkProperties } from 'utils/valueChecker';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
  input: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: 1,
    padding: '1rem 0',
  },
  header: {
    color: '#9f9bc8',
    border: '1px solid #9f9bc8',
    marginTop: '0.5rem',
  },
  values: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  value: {
    width: '17%',
    textAlign: 'center',
    padding: '0.5rem 0',
  },
}));

const MultiSelectInput = ({
  required,
  label,
  selectedTypes,
  value = [],
  defaultValues,
  onChange,
  exception,
  info,
}) => {
  const [types, setTypes] = useState(
    selectedTypes.reduce(
      (acc, item) => ({
        ...acc,
        [item.field]: item.type,
      }),
      {},
    ),
  );
  const [values, setValues] = useState(defaultValues);
  const [touched, setTouched] = useState({});
  const handleChange = evt => {
    const key = evt.target.name;
    const { value } = evt.target;

    if (key === exception) {
      const newTypes = Object.keys(value).reduce(
        (acc, item) => ({
          ...acc,
          [item]: 'text',
        }),
        {},
      );
      setTypes({ ...types, ...newTypes });
      setValues({
        ...values,
        ...value,
        [key]: value,
      });
    } else {
      const { type } = find(selectedTypes, ['field', key]);
      setTypes({ ...types, [key]: type });
      setValues({
        ...values,
        [key]: value,
      });
    }
  };

  const setFieldValue = (key, val) => {
    const { type } = find(selectedTypes, ['field', key]);
    setTypes({ ...types, [key]: type });
    setValues({
      ...values,
      [key]: val,
    });
  };
  const setFieldTouched = key => setTouched({ ...touched, [key]: true });
  const handleBtnClick = () => {
    const addedValues = selectedTypes.reduce((acc, item) => {
      if (item.field === exception) {
        return {
          ...acc,
          [item.field]: {
            value: values[item.field][item.field],
            type: types[item.field],
          },
        };
      } else {
        return {
          ...acc,
          [item.field]: {
            value: values[item.field],
            type: types[item.field],
          },
        };
      }
    }, {});
    onChange(uniqWith([...value, addedValues], isEqual));

    setTouched({});
    setValues(defaultValues);
  };

  const removeAddedItem = text => {
    const remainingItems = value.filter(item => item !== text);
    onChange(remainingItems);
  };

  const classes = useStyles();

  const props = {
    values,
    touched,
    errors: {},
    setFieldTouched,
    handleChange,
    setFieldValue,
  };

  return (
    <div className={classes.container}>
      <InputLabel shrink required={required}>
        {label}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <div className={classes.input}>
        {selectedTypes.map((field, index) =>
          fieldSelection({ item: field, key: index, id: index, props }),
        )}
        {checkProperties(values) && (
          <Image src={images.addMedia.default} onClick={handleBtnClick} />
        )}
      </div>
      {value.length > 0 && (
        <section>
          <InputLabel shrink required={required}>
            {`Added ${label}`}
          </InputLabel>
          <section className={classes.values}>
            {Object.keys(value[0]).map((item, key) => {
              return (
                <div className={cn(classes.value, classes.header)} key={key}>
                  {startCase(item)}
                </div>
              );
            })}
          </section>
          {value.map((items, itemIndex) => (
            <section className={classes.values} key={itemIndex}>
              {Object.keys(value[0]).map((item, index) => {
                return (
                  <div className={classes.value} key={index}>
                    <DetailField
                      type={items[item].type}
                      value={items[item].value}
                    />
                  </div>
                );
              })}
              <IconButton
                color="secondary"
                component="div"
                className={classes.icon}
                onClick={() => removeAddedItem(items)}
              >
                <DeleteOutlineIcon color="disabled" />
              </IconButton>
            </section>
          ))}
        </section>
      )}
    </div>
  );
};

MultiSelectInput.propTypes = {
  required: PropTypes.bool,
  label: PropTypes.string,
  selectedTypes: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.array,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func,
  exception: PropTypes.string,
  info: InfoPropTypes,
};

export default MultiSelectInput;
